import { Component, ElementRef, NgZone, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core'
import { Router, NavigationEnd } from '@angular/router'

import { Platform, AlertController } from '@ionic/angular/standalone'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'
import { CanDeactivateState } from './core/can-deactivate.guard'
import { filter, map, tap } from 'rxjs/operators'

import firebase from 'firebase/compat/app'

import { Title } from '@angular/platform-browser'
import { environment } from 'src/environments/environment'
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker'

import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { CameraZoomData, FirednaQrtagService } from './services/firedna-qrtag.service'
import { addIcons } from 'ionicons'
import {
  close,
  apps,
  chevronBackOutline,
  businessOutline,
  optionsOutline,
  peopleCircleOutline,
  arrowRedoOutline,
  eyeOffOutline,
  analyticsOutline,
  bulbOutline,
  documentsOutline,
  cloudUploadOutline,
  helpCircleOutline,
  logOutOutline,
  addCircleOutline,
  eyeOutline,
  trashOutline,
  arrowRedo,
  addCircle,
  toggle,
  createOutline,
  paperPlaneOutline,
  fingerPrintOutline,
  addOutline,
  documentTextOutline,
  flaskOutline,
  qrCodeOutline,
  alertCircleOutline,
  checkmarkOutline,
  arrowForwardCircleOutline,
  closeCircleOutline,
  documentAttachOutline,
  cogOutline,
  menu,
  saveOutline,
  arrowBackOutline,
  arrowForwardOutline,
  trash,
  cloudOfflineOutline,
  settingsOutline,
  logInOutline,
  wifi,
  peopleOutline,
  layersOutline,
  ribbonOutline,
  checkmarkCircle,
  alertCircle,
  chevronForwardOutline,
  qrCode,
  fingerPrint,
  keypad,
  closeOutline,
  menuOutline,
  checkmarkCircleOutline,
  cameraOutline,
  imagesOutline,
  pricetagsOutline,
  refresh,
  remove,
  banOutline,
  listOutline,
  checkboxOutline,
  checkmarkDoneCircle,
  checkmarkDoneOutline,
} from 'ionicons/icons'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { LiveUpdateService } from './services/live-update.service'
import { ModalService } from './services/modal.service'
import { MultiPhotoService } from './services/multi-photo.service'
import { AppInsightsService } from './services/app-insights.service'
import { ImageCacheService } from './services/image-cache.service'
import { SsoReturnService } from './services/sso-return.service'
import { PendingCaptureService } from './services/pending-capture.service'
import { PluginListenerHandle } from '@capacitor/core'

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  currentApplicationVersion = environment.appVersion
  serial: string = ''
  public cameraZoomData$: Observable<CameraZoomData>
  public capturedPhotos$: Observable<string[]>

  @ViewChild('modalwrapper') modalwrapper: ElementRef
  private restoredResultListener: PluginListenerHandle | null = null

  constructor(
    private platform: Platform,
    public alertController: AlertController,
    private router: Router,
    private zone: NgZone,
    private qrTagService: FirednaQrtagService,
    private multiPhotoService: MultiPhotoService,
    public updates: SwUpdate,
    public swUpdate: SwUpdate,
    private http: HttpClient,
    private titleService: Title,
    private appInsightsService: AppInsightsService,
    private liveUpdateService: LiveUpdateService,
    private modalService: ModalService,
    private imageCacheService: ImageCacheService,
    private ssoReturnService: SsoReturnService,
    private pendingCaptureService: PendingCaptureService
  ) {
    this.initializeApp()
    addIcons({
      close,
      apps,
      chevronBackOutline,
      businessOutline,
      optionsOutline,
      peopleCircleOutline,
      arrowRedoOutline,
      eyeOffOutline,
      analyticsOutline,
      bulbOutline,
      documentsOutline,
      cloudUploadOutline,
      helpCircleOutline,
      logOutOutline,
      addCircleOutline,
      eyeOutline,
      trashOutline,
      arrowRedo,
      addCircle,
      toggle,
      createOutline,
      paperPlaneOutline,
      fingerPrintOutline,
      addOutline,
      documentTextOutline,
      flaskOutline,
      qrCodeOutline,
      alertCircleOutline,
      checkmarkOutline,
      arrowForwardCircleOutline,
      closeCircleOutline,
      documentAttachOutline,
      cogOutline,
      menu,
      saveOutline,
      arrowBackOutline,
      arrowForwardOutline,
      trash,
      cloudOfflineOutline,
      settingsOutline,
      logInOutline,
      pricetagsOutline,
      wifi,
      peopleOutline,
      layersOutline,
      ribbonOutline,
      checkmarkCircle,
      alertCircle,
      chevronForwardOutline,
      closeOutline,
      qrCode,
      fingerPrint,
      keypad,
      menuOutline,
      checkmarkCircleOutline,
      cameraOutline,
      imagesOutline,
      refresh,
      remove,
      banOutline,
      listOutline,
      checkboxOutline,
      checkmarkDoneCircle,
      checkmarkDoneOutline,
    })
    window.onpopstate = () => (CanDeactivateState.defendAgainstBrowserBackButton = true)
    router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        tap(() => (CanDeactivateState.defendAgainstBrowserBackButton = false))
      )
      .subscribe()
  }
  async ngOnInit() {
    await this.liveUpdateService.liveUpdateReady()
    if (this.swUpdate.isEnabled && environment.serviceWorkersEnabled && !Capacitor.isNativePlatform()) {
      this.swUpdate.versionUpdates
        .pipe(
          tap((evt): any => console.log('swUpdate event', evt)),
          filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
          map(() => this.updateApp())
        )
        .subscribe()
    }

    if (Capacitor.isNativePlatform()) {
      this.liveUpdateService.checkStoreUpdate()
      await this.liveUpdateService.showCheckForUpdates(environment.production)
    }
  }

  ngAfterViewInit() {
    this.modalService.setModalWrapper(this.modalwrapper)
  }

  ngOnDestroy() {
    // Clean up periodic update checks
    this.liveUpdateService.stopPeriodicChecks()
    this.restoredResultListener?.remove()
  }

  closeQrCode() {
    this.qrTagService.closeQrCode()
  }
  zoom(dir: string) {
    this.qrTagService.zoom(dir)
  }
  updateApp() {
    this.liveUpdateService.fakeUpdateAvailable()
    console.log('The app is updating right now')
  }

  cameraCapture() {
    this.multiPhotoService.capturePhoto()
  }

  cameraPreviewComplete() {
    this.multiPhotoService.completeMultiPhotoSession()
  }
  cameraPreviewCancel() {
    this.multiPhotoService.cancelMultiPhotoSession()
  }

  initializeApp() {
    this.titleService.setTitle('Fire DNA | v' + this.currentApplicationVersion)
    // Stamp native version / bundle / network onto all telemetry. Fire-and-forget; never throws.
    void this.appInsightsService.initNativeContext()

    // Android re-delivers a Camera result here when the activity was torn down while the camera
    // app was in front (renderer OOM → recreate(), or a process kill). Registered before
    // platform.ready() on purpose: the native side retains the event only until the first listener,
    // and the photo must be copied out of the cache dir before anything can reload the WebView.
    App.addListener('appRestoredResult', (event) => {
      this.zone.run(() => void this.pendingCaptureService.handleRestoredResult(event))
    }).then((handle) => (this.restoredResultListener = handle))

    App.addListener('appUrlOpen', (event: any) => {
      this.zone.run(async () => {
        let url: URL
        try {
          url = new URL(event.url)
        } catch {
          return
        }
        const serial = url.searchParams.get('serial')
        const returnUrl = url.searchParams.get('return_url')
        if (serial) {
          this.serial = serial
          this.router.navigateByUrl('/public?serial=' + serial)
        } else if (returnUrl) {
          // SSO handoff from an external site (e.g. WordPress support site):
          // if already signed in, bounce straight back with a token; otherwise
          // fall through to the landing login which completes the redirect.
          await this.platform.ready()
          const validated = this.ssoReturnService.validateReturnUrl(returnUrl)
          if (!validated) {
            this.router.navigateByUrl('/landing')
            return
          }
          const redirected = await this.ssoReturnService.redirectWithToken(validated)
          if (!redirected) {
            this.router.navigateByUrl('/landing' + url.search)
          }
        } else {
          this.router.navigateByUrl(url.pathname + url.search || '/')
        }
      })
    })

    this.platform.ready().then(() => {
      // this.auth.startUpAsync();
      SplashScreen.hide()
      if (Capacitor.getPlatform() === 'android') {
        // Android 15+ (targetSdk 36) is edge-to-edge: Capacitor's core SystemBars plugin
        // insets the webview and pads for the keyboard. The status bar is transparent over
        // our white toolbar, so use dark icons, same as iOS. setBackgroundColor is a no-op
        // on 15+ and paints the bar white on older Android so the look is consistent.
        StatusBar.setBackgroundColor({ color: '#ffffff' })
        StatusBar.setStyle({ style: Style.Light })
      } else if (Capacitor.getPlatform() === 'ios') {
        StatusBar.setOverlaysWebView({ overlay: true })
        StatusBar.setStyle({ style: Style.Light })
      }

      // setup firebase for user authentication
      firebase.initializeApp(environment.firebaseConfig)
      firebase.auth().onAuthStateChanged((user) => {
        if (user) {
          this.imageCacheService.clearNotFoundCache()
        }
      })
      App.addListener('appStateChange', async ({ isActive }) => {
        console.log('App state changed. Is active?', isActive)
        if (isActive) {
          // App resumed from background - check for updates
          await this.liveUpdateService.silentCheckForUpdates()
        }
      })
      this.cameraZoomData$ = this.qrTagService._currentCameraData$
      this.capturedPhotos$ = this.multiPhotoService.capturedPhotos$

      // Start periodic update checks (every 30 minutes)
      if (Capacitor.isNativePlatform()) {
        this.liveUpdateService.startPeriodicChecks()
      }
    })
  }
}
